import { useMemo, useState } from "react";
import { Loader2, Download, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import type { MediaAsset } from "./MediaPicker";

// Vite-resolved URLs for every bundled image in src/assets.
// Survives build because Vite rewrites these to hashed asset URLs.
const BUNDLED = import.meta.glob("/src/assets/**/*.{jpg,jpeg,png,webp}", {
  eager: true,
  query: "?url",
  import: "default",
}) as Record<string, string>;

type Status = "pending" | "uploading" | "skipped" | "done" | "error";
interface Row {
  filePath: string;
  fileName: string;
  url: string;
  selected: boolean;
  status: Status;
  message?: string;
}

const guessMime = (name: string): string => {
  const ext = name.toLowerCase().split(".").pop();
  if (ext === "png") return "image/png";
  if (ext === "webp") return "image/webp";
  return "image/jpeg";
};

const altFromName = (name: string) =>
  name
    .replace(/\.[a-z0-9]+$/i, "")
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImported: (assets: MediaAsset[]) => void;
}

const ImportBundledAssets = ({ open, onOpenChange, onImported }: Props) => {
  const { user } = useAdminAuth();
  const [running, setRunning] = useState(false);
  const [existingNames, setExistingNames] = useState<Set<string>>(new Set());
  const [rows, setRows] = useState<Row[]>(() =>
    Object.entries(BUNDLED)
      .map(([filePath, url]) => ({
        filePath,
        fileName: filePath.split("/").pop() ?? filePath,
        url,
        selected: true,
        status: "pending" as Status,
      }))
      .sort((a, b) => a.fileName.localeCompare(b.fileName)),
  );

  // Lazy-load existing media filenames once when dialog opens
  const initExisting = async () => {
    const { data } = await supabase
      .from("media_assets")
      .select("file_name")
      .eq("usage_label", "Imported site asset");
    const set = new Set((data ?? []).map((r) => (r.file_name ?? "").toLowerCase()));
    setExistingNames(set);
    setRows((prev) =>
      prev.map((r) =>
        set.has(r.fileName.toLowerCase())
          ? { ...r, selected: false, status: "skipped", message: "Already imported" }
          : r,
      ),
    );
  };

  const summary = useMemo(() => {
    const total = rows.length;
    const selected = rows.filter((r) => r.selected).length;
    const done = rows.filter((r) => r.status === "done").length;
    const skipped = rows.filter((r) => r.status === "skipped").length;
    const errored = rows.filter((r) => r.status === "error").length;
    return { total, selected, done, skipped, errored };
  }, [rows]);

  const updateRow = (filePath: string, patch: Partial<Row>) =>
    setRows((prev) => prev.map((r) => (r.filePath === filePath ? { ...r, ...patch } : r)));

  const importOne = async (row: Row): Promise<MediaAsset | null> => {
    updateRow(row.filePath, { status: "uploading", message: undefined });
    try {
      const res = await fetch(row.url);
      if (!res.ok) throw new Error(`Fetch failed (${res.status})`);
      const blob = await res.blob();
      const mime = blob.type || guessMime(row.fileName);
      const now = new Date();
      const yyyy = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, "0");
      const path = `imported/${yyyy}/${mm}/${row.fileName}`;

      const { error: upErr } = await supabase.storage
        .from("media")
        .upload(path, blob, { contentType: mime, upsert: false });
      if (upErr && !/(exists|duplicate)/i.test(upErr.message)) throw upErr;

      const { data: pub } = supabase.storage.from("media").getPublicUrl(path);

      const { data, error } = await supabase
        .from("media_assets")
        .insert({
          bucket: "media",
          path,
          public_url: pub.publicUrl,
          file_name: row.fileName,
          file_type: mime,
          file_size: blob.size,
          alt_text: altFromName(row.fileName),
          usage_label: "Imported site asset",
          created_by: user?.id ?? null,
          updated_by: user?.id ?? null,
        } as never)
        .select()
        .single();
      if (error) throw error;

      updateRow(row.filePath, { status: "done" });
      return data as MediaAsset;
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Unknown error";
      updateRow(row.filePath, { status: "error", message: msg });
      return null;
    }
  };

  const runImport = async () => {
    if (running) return;
    setRunning(true);
    const queue = rows.filter((r) => r.selected && r.status !== "done" && r.status !== "skipped");
    const imported: MediaAsset[] = [];
    // Sequential to avoid hammering storage
    for (const r of queue) {
      const a = await importOne(r);
      if (a) imported.push(a);
    }
    setRunning(false);
    if (imported.length) {
      onImported(imported);
      // refresh dedupe set
      setExistingNames((prev) => {
        const next = new Set(prev);
        imported.forEach((a) => next.add((a.file_name ?? "").toLowerCase()));
        return next;
      });
      toast.success(`Imported ${imported.length} asset${imported.length === 1 ? "" : "s"}`);
    } else {
      toast.message("Nothing imported");
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (o && existingNames.size === 0) void initExisting();
      }}
    >
      <DialogContent className="max-w-3xl rounded-none">
        <DialogHeader>
          <DialogTitle className="font-headline text-2xl">
            Import bundled site assets
          </DialogTitle>
          <DialogDescription>
            Copy images that ship with the site code into the Media Library so they can be reused
            from pickers. Already-imported files are skipped automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center justify-between gap-2 border border-border bg-muted/30 p-3 text-xs">
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <span>Total: {summary.total}</span>
            <span>Selected: {summary.selected}</span>
            <span>Imported this session: {summary.done}</span>
            <span>Already in library: {summary.skipped}</span>
            {summary.errored ? (
              <span className="text-destructive">Errors: {summary.errored}</span>
            ) : null}
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-none"
              disabled={running}
              onClick={() =>
                setRows((prev) =>
                  prev.map((r) =>
                    r.status === "skipped" || r.status === "done"
                      ? r
                      : { ...r, selected: true },
                  ),
                )
              }
            >
              Select all
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-none"
              disabled={running}
              onClick={() => setRows((prev) => prev.map((r) => ({ ...r, selected: false })))}
            >
              Deselect all
            </Button>
            <Button
              type="button"
              size="sm"
              variant="gold"
              className="rounded-none"
              disabled={running || summary.selected === 0}
              onClick={runImport}
            >
              {running ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              Import selected
            </Button>
          </div>
        </div>

        <div className="max-h-[55vh] overflow-y-auto border border-border">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-card">
              <tr className="border-b border-border text-left">
                <th className="w-10 px-2 py-2"></th>
                <th className="px-2 py-2">File</th>
                <th className="w-32 px-2 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.filePath} className="border-b border-border/60">
                  <td className="px-2 py-1.5">
                    <input
                      type="checkbox"
                      checked={r.selected}
                      disabled={running || r.status === "skipped" || r.status === "done"}
                      onChange={(e) => updateRow(r.filePath, { selected: e.target.checked })}
                    />
                  </td>
                  <td className="truncate px-2 py-1.5" title={r.filePath}>
                    {r.fileName}
                  </td>
                  <td className="px-2 py-1.5">
                    <StatusPill status={r.status} message={r.message} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-[11px] text-muted-foreground">
          This duplicates the file into Lovable Cloud Storage. The original bundled asset is not
          removed. Re-running this is safe — files already in the library are skipped.
        </p>
      </DialogContent>
    </Dialog>
  );
};

const StatusPill = ({ status, message }: { status: Status; message?: string }) => {
  const styles: Record<Status, string> = {
    pending: "bg-muted text-muted-foreground",
    uploading: "bg-accent/20 text-accent",
    done: "bg-emerald-100 text-emerald-700",
    skipped: "bg-muted text-muted-foreground",
    error: "bg-destructive/15 text-destructive",
  };
  const label: Record<Status, string> = {
    pending: "Pending",
    uploading: "Uploading…",
    done: "Imported",
    skipped: "In library",
    error: "Failed",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.1em] ${styles[status]}`}
      title={message}
    >
      {status === "uploading" ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
      {status === "error" ? <X className="h-3 w-3" /> : null}
      {label[status]}
    </span>
  );
};

export default ImportBundledAssets;
